import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import http from "../../../commonFunctions/httpClient";
import WSButton from "../../../Components/Button/WSButton";
import logo from "../../../images/logo_invoice.svg";

interface ScanUser {
  customerProfileId: string;
  name: string;
}

interface ScanTest {
  userId: string;
  userName: string;
  testName: string;
}

interface ScanBookingInfo {
  bookingId: string;
  bookedOn: string;
  lab: {
    labId: string;
    labName: string;
  };
  payment: {
    method: string;
    amount: number;
  };
  tests: ScanTest;
  slot: {
    date: string;
    time: string;
  };
}

interface OrderTracking {
  orderId: string;
  trackingId: string;
  steps: Array<{ status: string }>;
}

interface ScanResult {
  userId: string;
  userName: string;
  resultAvailable: boolean;
  reportPdfUrl: string | null;
}

interface OrderSummary {
  itemTotal: number;
  discount: number;
  totalPayable: number;
  savings: number;
  mrp:number;
}

interface ScanBookingResponse {
  status: boolean;
  data: {
    users: ScanUser[];
    scanBookingInfo: ScanBookingInfo[];
    results: ScanResult[];
    orderSummary: OrderSummary[];
    orderTracking: OrderTracking[];
  };
}

interface LabScanInvoiceProps {
  bookingId: string;
}

export const LabScanInvoice: React.FC<LabScanInvoiceProps> = ({ bookingId }) => {
  const [scanData, setScanData] = useState<ScanBookingResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchScanData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 Fetching scan booking for bookingId:", bookingId);
      
      const response: ScanBookingResponse = await http.get(
        `lab/LabScanBooking/${bookingId}`  // ✅ YOUR SCAN API
      );
      
      console.log("📊 FULL SCAN BOOKING API RESPONSE:", response);
      
      if (response.status) {
        setScanData(response.data);
      } else {
        setError("No valid scan booking found for this booking ID");
        setScanData(null);
      }
      
    } catch (error: any) {
      console.error("❌ SCAN BOOKING API ERROR:", error);
      setError("Failed to fetch scan booking data");
      setScanData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchScanData();
    }
  }, [fetchScanData]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatTimeSlot = (timeSlot: string) => {
    return timeSlot.replace(/[:-]/g, ' - ');
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading scan invoice...</Typography>
      </Box>
    );
  }

  if (error || !scanData) {
    return (
      <Box p={4}>
        <Alert severity="error">
          {error || "No scan booking data found for this booking ID"}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Booking ID: {bookingId}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  const firstBooking = scanData.scanBookingInfo[0] || {};
  const firstUser = scanData.users[0] || { name: "Unknown" };
  const firstResult = scanData.results[0] || { resultAvailable: false };
  const firstSummary = scanData.orderSummary[0] || { itemTotal: 0, discount: 0, totalPayable: 0, savings: 0 };
  const firstTracking = scanData.orderTracking[0] || { trackingId: "" };
  const scanTest = firstBooking.tests || { testName: "N/A", userName: "N/A" };

  return (
    <Box p={4} sx={{ backgroundColor: "#fff", maxWidth: "100%" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <WSButton
          id="printScanInvoiceBtn"
          size="small"
          label={`Print Scan Invoice #${bookingId.slice(-6).toUpperCase()}`}
          onClick={handlePrint}
        />
      </Box>

      <Box
        ref={contentRef}
        sx={{ 
          p: 4, 
          width: "100%", 
          maxWidth: "800px", 
          margin: "0 auto",
          bgcolor: "white",
          '@media print': {
            maxWidth: "none",
            width: "100%",
            p: 2,
            margin: 0
          }
        }}
      >
        {/* HEADER */}
        <Grid container sx={{ mb: 3 }}>
          {/* LOGO - TOP CENTER */}
          <Grid item xs={12} textAlign="left" sx={{ mb: -4 }}>
            <Box
              component="img"
              src={logo}
              alt="MELP Scan Logo"
              sx={{
                width: 100,
                height: 100,
                borderRadius: "12px",
                objectFit: "contain",
                '@media print': {
                  filter: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }
              }}
              onError={(e: any) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Grid>
          
          {/* BOTTOM ROW - MELP Scan LEFT | EMPTY RIGHT */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={6}>
                <Typography fontSize={20} fontWeight="bold" sx={{ color: "#098963", mb: 1 }}>
                  MELP Scan
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Medical Imaging & Scan Services
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  123 Health Street, Kerala - 670001
                </Typography>
                <Typography variant="body2">
                  Phone: +91 98765 43210
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ minHeight: 80 }} />
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ORDER DETAILS */}
        <Grid container justifyContent="space-between" mb={3}>
          <Grid item xs={6}>
            <Typography fontSize={18} gutterBottom sx={{ fontWeight: 700 }}>
              Scan Invoice #{bookingId.slice(-6).toUpperCase()}
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Customer:</strong> {firstUser.name}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Customer ID:</strong> {firstUser.customerProfileId?.slice(-8)}
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <strong>Lab:</strong> {firstBooking.lab?.labName}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Scan Type:</strong> {scanTest.testName}
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography fontSize={18} sx={{ fontWeight: 700, mb: 2 }}>
              <strong>Date:</strong> {formatDate(firstBooking.bookedOn)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Slot:</strong> {formatDate(firstBooking.slot.date)} {formatTimeSlot(firstBooking.slot.time)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Payment:</strong> {firstBooking.payment?.method || 'N/A'}
            </Typography>
            {firstTracking.trackingId && (
              <Chip
                label={firstTracking.trackingId}
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Grid>
        </Grid>

        {/* SCANS TABLE */}
        <TableContainer sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#098963" }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>Scan Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Price</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scanData.scanBookingInfo.length > 0 ? (
                scanData.scanBookingInfo.map((booking: ScanBookingInfo, index: number) => (
                  <TableRow key={booking.bookingId + index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Typography variant="body2">{booking.tests.testName}</Typography>
                    </TableCell>
                    <TableCell align="center">{booking.tests.userName}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(firstSummary.mrp)}
                    </TableCell>
                    <TableCell align="center">1</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No scans found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* BILL SUMMARY */}
        <Box sx={{ borderTop: "3px solid #098963", pt: 3, mb: 4 }}>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="right">
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  <strong>Items Total:</strong> {formatCurrency(firstSummary.itemTotal)}
                </Typography>
                {firstSummary.discount > 0 && (
                  <Typography variant="h6" color="error" sx={{ fontWeight: 700, mb: 1 }}>
                    <strong>Discount:</strong> -{formatCurrency(firstSummary.discount)}
                  </Typography>
                )}
                <Divider sx={{ my: 1, borderWidth: 2 }} />
                <Typography fontSize={20} color="#098963" sx={{ fontWeight: 900, mb: 1 }}>
                  <strong>Total Payable:</strong> {formatCurrency(firstSummary.totalPayable)}
                </Typography>
                <Typography variant="body2" color="success.main">
                  <strong>Savings:</strong> {formatCurrency(firstSummary.savings)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* RESULTS STATUS */}
        {scanData.results.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Grid container justifyContent="center">
              <Grid item xs={12} textAlign="center">
                <Typography variant="h6" sx={{ mb: 1, color: scanData.results[0].resultAvailable ? "success.main" : "warning.main" }}>
                  {scanData.results[0].resultAvailable ? "✅ Scan Report Available" : "⏳ Scan Report Pending"}
                </Typography>
                {scanData.results[0].resultAvailable && scanData.results[0].reportPdfUrl && (
                  <Typography variant="body2" sx={{ color: "primary.main" }}>
                    Report ready for download
                  </Typography>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* FOOTER */}
        <Divider sx={{ my: 4 }} />
        <Box textAlign="center">
          <Typography variant="h5" color="#098963" sx={{ fontWeight: 700, mb: 2 }}>
            🩻 Precision Imaging. Expert Analysis. Fast Results. 💙
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Thank you for choosing MELP Scan | Kerala
          </Typography>
          <Typography sx={{ 
            fontSize: '0.8rem', 
            color: 'text.secondary', 
            mt: 2,
            '@media print': { fontSize: '0.9rem' }
          }}>
            * This is a computer-generated scan invoice
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LabScanInvoice;
